import React, { FC, useEffect, useState } from "react";
import { Autocomplete, TextField } from "@mui/material";
import { IUiProps } from "../../../../types/uiProps";

interface ISelect {
  variants: string[];
  onChange: (e: any) => void;
  name?: string;
  errorMessage?: string;
  isError?: boolean;
  defaultValue?: string;
  placeholder?: string;
  uiProps?: IUiProps;
  isRequired?: boolean;
}

export const Select: FC<ISelect> = React.memo(
  ({
    variants,
    onChange,
    name,
    defaultValue,
    placeholder,
    uiProps,
    isRequired,
  }) => {
    // Uncontrolled selection, matching the original: only the displayed
    // text is tracked here, the actual "selected value" lives entirely in
    // the caller's own form state via onChange - so a defaultValue that
    // isn't one of `variants` (every real caller passes "-", a stand-in
    // for "nothing chosen yet") can still be shown without Autocomplete
    // complaining about an invalid controlled `value`.
    const [inputValue, setInputValue] = useState(defaultValue || variants[0] || "");

    useEffect(() => {
      onChange({ target: { name, value: defaultValue || variants[0] || "" } });
      // Fired once on mount only, mirroring the original's mount-only effect.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
      <Autocomplete
        options={variants}
        inputValue={inputValue}
        onInputChange={(_, newValue, reason) => {
          if (reason === "input") {
            setInputValue(newValue);
          }
        }}
        onChange={(_, selected) => {
          const value = selected ?? "";
          setInputValue(value);
          onChange({ target: { name, value } });
        }}
        size="small"
        style={uiProps as React.CSSProperties}
        sx={{ my: "10px" }}
        renderInput={(params) => (
          <TextField {...params} label={placeholder} required={isRequired} />
        )}
      />
    );
  }
);
